import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getCurrentHostWithPortAndProtocol,
  isShopifyLoginDomain,
  isValidMyshopifyShopDomain,
  normalizeShopifyShopInput,
} from "../../../../utils/shopify-login";
import { translations } from "../../../../utils/language";
import { isVisibleScript } from "../../../../utils/displayScript";
import { BoxFormLogin } from "../home/styled";
import InputCustom from "../../components/InputCustom";
import ErrorMessage from "../../components/ErrorInput";
import { Container as BtnConnect } from "../../components/Btn/styled";
import { SiShopify } from "react-icons/si";
import {
  Container,
  Body,
  SectionLeft,
  SectionRight,
  Rodape,
  BoxOptions,
  Contents,
  SectionLogo,
} from "../../components/Page/styled";
import animation from "../../../../assets/animation/dijitalLogin.webm";

/**
 * Inicia OAuth de instalação (igual a `integrateShopify` no Modal de integrações).
 * @returns {string | null} mensagem de erro ou `null` se o redirect foi disparado
 */
function goToShopifyOAuthInstall(normalizedShop) {
  if (!process.env.REACT_APP_SHOPIFY_CLIENT_ID) {
    return "REACT_APP_SHOPIFY_CLIENT_ID não configurado.";
  }

  if (!normalizedShop || !isValidMyshopifyShopDomain(normalizedShop)) {
    return "Loja Shopify inválida. Use o formato minhaloja.myshopify.com.";
  }

  const state = `${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  localStorage.setItem("shopify_oauth_state", state);

  const redirectUri = getCurrentHostWithPortAndProtocol() + '/integracoes/shopify';
  const oauthUrl = `https://${normalizedShop}/admin/oauth/authorize?client_id=${process.env.REACT_APP_SHOPIFY_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}&grant_options[]=`;
  window.location.href = oauthUrl;
  return null;
}

const eplusLoginUrl = () => {
  const base = process.env.REACT_APP_EPLUS_DOMAIN || "";
  if (!base) return "";
  return `${base.replace(/\/$/, "")}/login/dijital`;
};

function shopifyLoginLang(reduxData) {
  if (typeof reduxData === "string" && reduxData) return reduxData;
  if (typeof window !== "undefined") {
    const s = localStorage.getItem("language");
    if (s) return s;
  }
  return "pt";
}

function ShopLoginLayout({ children }) {
  const dispatch = useDispatch();
  const reduxLanguage = useSelector((state) => state.reducerLanguage);

  useEffect(() => {
    const storageLanguage = localStorage.getItem("language");
    if (storageLanguage) {
      dispatch({
        type: "SAGA_LIST_LANGUAGE",
        payload: { language: storageLanguage },
      });
    } else {
      dispatch({
        type: "SAGA_LIST_LANGUAGE",
        payload: { language: "pt" },
      });
    }
  }, [dispatch]);

  const lang = shopifyLoginLang(reduxLanguage.data);

  useEffect(() => {
    const el = document.getElementById("shopify-login-animation");
    if (!el) return;
    const onEnter = function () {
      this.play();
    };
    el.addEventListener("mouseenter", onEnter, false);
    return () => el.removeEventListener("mouseenter", onEnter);
  }, []);

  return (
    <Container>
      <Body>
        <SectionLeft>
          <SectionLogo>
            <span id="box-animation">
              <video
                style={{ cursor: "pointer" }}
                id="shopify-login-animation"
                width="75"
                muted
              >
                <source src={animation} type="video/webm" />
              </video>
            </span>
          </SectionLogo>
          <a
            href="https://empreender.com.br/"
            style={{
              display: "flex",
              width: "fit-content",
              margin: "11px auto",
              padding: "0",
            }}
            target="_blank"
            rel="noreferrer"
          >
            <img
              src="https://empreender.nyc3.cdn.digitaloceanspaces.com/static/empreender.svg"
              alt="empreender-logo"
              style={{
                position: "unset",
                width: "fit-content",
                height: "9px",
                margin: "0",
              }}
            />
          </a>
        </SectionLeft>
        <SectionRight>
          <BoxOptions>
            <span
              style={{
                display: "inline-block",
                marginRight: "20px",
                paddingBottom: "5px",
                fontWeight: "bold",
                borderBottom: "2.5px solid #dc3545",
                marginTop: "2.5px",
                cursor: "default",
                userSelect: "none",
              }}
            >
              Login
            </span>
          </BoxOptions>
          <Contents>{children}</Contents>
          <Rodape>
            <a
              className="iubenda-nostyle no-brand iubenda-noiframe iubenda-embed iubenda-noiframe"
              href="https://www.iubenda.com/privacy-policy/88589789"
              title="Política de Privacidade"
            >
              {translations["Política de Privacidade"][lang]}
            </a>
            <a
              href="https://www.iubenda.com/privacy-policy/88589789/cookie-policy"
              className="iubenda-nostyle no-brand iubenda-noiframe iubenda-embed iubenda-noiframe"
              title="Política de Cookies"
            >
              {translations["Política de Cookie"][lang]}
            </a>
            <a
              href="https://www.iubenda.com/termos-e-condicoes/88589789"
              className="iubenda-nostyle no-brand iubenda-noiframe iubenda-embed iubenda-noiframe"
              title="Termos e Condições"
            >
              {translations["Termos e condições"][lang]}
            </a>
          </Rodape>
        </SectionRight>
      </Body>
      {isVisibleScript(false)}
    </Container>
  );
}

const SHOPIFY_APP_STORE_URL = "https://apps.shopify.com/dijital";

export default function LoginShopify() {
  const language = useSelector((state) => state.reducerLanguage);
  const [isShopifyDomain, setIsShopifyDomain] = useState(() =>
    typeof window !== "undefined" ? isShopifyLoginDomain() : false
  );
  const [isDevMode, setIsDevMode] = useState(() =>
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("mode") === "dev"
      : false
  );
  const [shopifyStore, setShopifyStore] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const shopifyHost = isShopifyLoginDomain();
    setIsShopifyDomain(shopifyHost);

    const searchParams = new URLSearchParams(window.location.search);
    setIsDevMode(searchParams.get("mode") === "dev");

    if (shopifyHost) {
      const hmac = searchParams.get("hmac");
      const shop = searchParams.get("shop");
      const code = searchParams.get("code");
      if (hmac && shop && !code) {
        const err = goToShopifyOAuthInstall(normalizeShopifyShopInput(shop));
        if (err) setError(err);
      }
      return;
    }

    const base = eplusLoginUrl();
    if (!base) return;
    const currentUrl = new URL(window.location.href);
    const currentParams = new URLSearchParams(window.location.search);
    const hashFragment = currentUrl.hash;
    const destinationUrl = new URL(base);
    currentParams.forEach((value, key) => {
      if (!destinationUrl.searchParams.has(key)) destinationUrl.searchParams.set(key, value);
    });
    let finalUrl = destinationUrl.toString();
    if (hashFragment && !finalUrl.includes(hashFragment)) finalUrl += hashFragment;
    window.location.href = finalUrl;
  }, []);

  const startShopifyOauth = () => {
    const shop = normalizeShopifyShopInput(shopifyStore);
    if (!shop) {
      setError("Informe a URL da loja Shopify.");
      return;
    }
    if (!isValidMyshopifyShopDomain(shop)) {
      setError(
        "Use sua-loja.myshopify.com, só o nome da loja, ou um link do admin (admin.shopify.com/store/...)."
      );
      return;
    }
    setError("");
    const err = goToShopifyOAuthInstall(shop);
    if (err) setError(err);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isDevMode) {
      startShopifyOauth();
      return;
    }
    window.location.href = SHOPIFY_APP_STORE_URL;
  };

  if (!isShopifyDomain) {
    return (
      <ShopLoginLayout>
        <p style={{ textAlign: "center" }}>
          {translations["Aguarde..."][shopifyLoginLang(language.data)]}
        </p>
      </ShopLoginLayout>
    );
  }

  return (
    <ShopLoginLayout>
      <BoxFormLogin onSubmit={handleSubmit}>
        <h1 style={{ fontSize: "20px", fontWeight: 600, marginBottom: "8px" }}>
          Conectar com Shopify
        </h1>
        {isDevMode && (
          <>
            <p style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>
              Informe a loja: domínio myshopify ou o link do admin (ex.: admin.shopify.com/store/nome-da-loja).
            </p>
            <InputCustom>
              <span>
                <SiShopify size={20} color="#95BF47" />
              </span>
              <input
                name="shopifyStore"
                type="text"
                value={shopifyStore}
                onChange={(e) => setShopifyStore(e.target.value)}
                placeholder="sua-loja.myshopify.com ou admin.shopify.com/store/sua-loja"
                autoComplete="off"
              />
            </InputCustom>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </>
        )}

        <span style={{ marginTop: 16 }}>
          <BtnConnect type="submit" style={{ backgroundColor: "#4e0cb3" }}>
            Conectar com Shopify
          </BtnConnect>
        </span>
      </BoxFormLogin>
    </ShopLoginLayout>
  );
}
